import React, { useState, useEffect, useCallback } from 'react';
import { MSRCCvrfManager } from '../utils/MSRCCvrfManager';
import type { MSRCUpdateSummary } from '../types/msrc-cvrf';
import type { CvrfDocumentInfo } from '../types/msrc-cvrf';

function getCvrfDocumentId(item: MSRCUpdateSummary): string | null {
    if (item.Id) return item.Id;
    if (item.Alias) return item.Alias;
    return null;
}

function formatDate(value: string | undefined): string {
    if (!value) return '—';
    try {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return value;
    }
}

function severityBadgeClass(severity: string | undefined): string {
    if (!severity) return 'badge bg-secondary';
    const s = severity.toLowerCase();
    if (s.includes('critical')) return 'badge bg-danger';
    if (s.includes('important')) return 'badge bg-warning text-dark';
    if (s.includes('moderate') || s.includes('low')) return 'badge bg-info';
    return 'badge bg-secondary';
}

export interface MSRCInformationProps {
    /** Number of security update summaries to display. */
    count: number;
}

function MSRCInformation({ count }: MSRCInformationProps) {
    const [updates, setUpdates] = useState<MSRCUpdateSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [modalShow, setModalShow] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [modalDetail, setModalDetail] = useState<CvrfDocumentInfo | null>(null);
    const [modalTitle, setModalTitle] = useState<string>('CVRF Details');

    const handleViewCvrf = useCallback((item: MSRCUpdateSummary) => {
        const id = getCvrfDocumentId(item);
        if (!id) return;
        setModalTitle(item.DocumentTitle || item.Alias || item.Id || 'CVRF Details');
        setModalShow(true);
        setModalError(null);
        setModalDetail(null);
        setModalLoading(true);
        MSRCCvrfManager.getInstance()
            .getCvrfDocument(id)
            .then((data) => {
                setModalDetail(data);
                setModalError(null);
            })
            .catch((err) => {
                setModalError(err instanceof Error ? err.message : 'Failed to load CVRF document');
                setModalDetail(null);
            })
            .finally(() => {
                setModalLoading(false);
            });
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalShow(false);
        setModalDetail(null);
        setModalError(null);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        MSRCCvrfManager.getInstance()
            .getUpdates({
                orderBy: 'ID desc',
            })
            .then((data) => {
                if (!cancelled) setUpdates(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load security updates');
                    setUpdates([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [count]);

    if (loading) {
        return (
            <div className="msrc-information">
                <div className="text-center p-4">Loading security updates…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="msrc-information">
                <div className="text-center p-4 text-danger">Error: {error}</div>
            </div>
        );
    }

    const displayList = updates.slice(0, Math.max(0, count));
    if (displayList.length === 0) {
        return (
            <div className="msrc-information">
                <div className="text-center p-4 text-muted">No security updates to display.</div>
            </div>
        );
    }

    return (
        <div className="msrc-information">
            <div className="row g-3">
                {displayList.map((item) => (
                    <div key={item.Id ?? item.Alias ?? String(Math.random())} className="col-12 col-sm-6 col-lg-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h6 className="card-title mb-0 text-break">
                                        {item.DocumentTitle || item.Alias || item.Id || 'Security Update'}
                                    </h6>
                                    {item.Severity && (
                                        <span className={severityBadgeClass(item.Severity)}>
                                            {item.Severity}
                                        </span>
                                    )}
                                </div>
                                <div className="small text-muted mb-2">
                                    <span title="Initial release">{formatDate(item.InitialReleaseDate)}</span>
                                    {' · '}
                                    <span title="Current release">{formatDate(item.CurrentReleaseDate)}</span>
                                </div>
                                {(item.Id ?? item.Alias) && (
                                    <div className="small text-muted mb-2">
                                        <span className="fw-medium">{item.Id ?? item.Alias}</span>
                                    </div>
                                )}
                                {getCvrfDocumentId(item) && (
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                        onClick={() => handleViewCvrf(item)}
                                    >
                                        View CVRF
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {modalShow && (
                <div
                    className="modal fade show"
                    style={{ display: 'block', zIndex: 1050 }}
                    tabIndex={-1}
                    aria-labelledby="msrc-cvrf-modal-title"
                    aria-modal
                >
                    <div
                        className="modal-backdrop fade show"
                        style={{ zIndex: 1050 }}
                        onClick={handleCloseModal}
                        aria-hidden
                    />
                    <div
                        className="modal-dialog modal-lg modal-dialog-scrollable"
                        style={{ zIndex: 1055 }}
                    >
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="msrc-cvrf-modal-title">
                                {modalTitle}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={handleCloseModal}
                            />
                        </div>
                        <div className="modal-body">
                            {modalLoading && (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading…</span>
                                    </div>
                                </div>
                            )}
                            {modalError && !modalLoading && (
                                <div className="alert alert-danger mb-0">{modalError}</div>
                            )}
                            {modalDetail != null && !modalLoading && (
                                <div className="msrc-cvrf-detail small" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                                    {modalDetail.documentTitle && (
                                        <section className="mb-3">
                                            <h6 className="text-muted border-bottom pb-1">Document</h6>
                                            <p className="mb-1"><strong>Title:</strong> {modalDetail.documentTitle}</p>
                                            {modalDetail.documentType && <p className="mb-1"><strong>Type:</strong> {modalDetail.documentType}</p>}
                                            {modalDetail.publisherName && <p className="mb-1"><strong>Publisher:</strong> {modalDetail.publisherName}</p>}
                                            {modalDetail.publisherContact && <p className="mb-1"><strong>Contact:</strong> {modalDetail.publisherContact}</p>}
                                        </section>
                                    )}
                                    {modalDetail.tracking && (modalDetail.tracking.id ?? modalDetail.tracking.currentReleaseDate) && (
                                        <section className="mb-3">
                                            <h6 className="text-muted border-bottom pb-1">Tracking</h6>
                                            {modalDetail.tracking.id && <p className="mb-1"><strong>ID:</strong> {modalDetail.tracking.id}</p>}
                                            {modalDetail.tracking.status && <p className="mb-1"><strong>Status:</strong> {modalDetail.tracking.status}</p>}
                                            {modalDetail.tracking.version && <p className="mb-1"><strong>Version:</strong> {modalDetail.tracking.version}</p>}
                                            {modalDetail.tracking.initialReleaseDate && <p className="mb-1"><strong>Initial release:</strong> {formatDate(modalDetail.tracking.initialReleaseDate)}</p>}
                                            {modalDetail.tracking.currentReleaseDate && <p className="mb-1"><strong>Current release:</strong> {formatDate(modalDetail.tracking.currentReleaseDate)}</p>}
                                            {modalDetail.tracking.revisions && modalDetail.tracking.revisions.length > 0 && (
                                                <div className="mt-2">
                                                    <strong>Revisions:</strong>
                                                    <ul className="mb-0 ps-3">
                                                        {modalDetail.tracking.revisions.map((r, i) => (
                                                            <li key={i}>
                                                                {r.number && `#${r.number} `}{r.date && formatDate(r.date)}{r.description && ` – ${r.description}`}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </section>
                                    )}
                                    {modalDetail.notes.length > 0 && (
                                        <section className="mb-3">
                                            <h6 className="text-muted border-bottom pb-1">Release notes</h6>
                                            <ul className="mb-0 ps-3">
                                                {modalDetail.notes.map((n, i) => (
                                                    <li key={i}>
                                                        {n.title && <strong>{n.title}: </strong>}
                                                        <span dangerouslySetInnerHTML={{ __html: n.value }} />
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}
                                    {modalDetail.products.length > 0 && (
                                        <section className="mb-3">
                                            <h6 className="text-muted border-bottom pb-1">Products</h6>
                                            <ul className="mb-0 ps-3">
                                                {modalDetail.products.map((p, i) => (
                                                    <li key={i}>{p.fullProductName ?? p.productId}</li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}
                                    {modalDetail.vulnerabilities.length > 0 && (
                                        <section className="mb-3">
                                            <h6 className="text-muted border-bottom pb-1">Vulnerabilities</h6>
                                            {modalDetail.vulnerabilities.map((v, i) => (
                                                <div key={i} className="mb-2 pb-2 border-bottom">
                                                    {v.title && <p className="mb-1"><strong>{v.title}</strong></p>}
                                                    {v.cve && <p className="mb-1"><span className="badge bg-secondary">{v.cve}</span></p>}
                                                    {v.notes && v.notes.length > 0 && (
                                                        <ul className="mb-0 ps-3 small">
                                                            {v.notes.map((note, j) => <li key={j}>{note}</li>)}
                                                        </ul>
                                                    )}
                                                </div>
                                            ))}
                                        </section>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                Close
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MSRCInformation;
